import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Box,
    Alert,
    FormControlLabel,
    Switch,
    Stack,
} from '@mui/material';
import { useUpsertCptMutation, useDeleteCptMutation } from '../../../../../../services/ContractService';
import { CptView } from '../../../../../../services/ContractService/types/contractTypes';
import { useCptForm } from './hooks';
import {
    CodeApcFields,
    CategoryGrouperFields,
    RateWeightFields,
    DialogActionButtons,
    DeleteConfirmDialog
} from './components';

interface AddEditCptDialogProps {
    open: boolean;
    editingCpt: CptView | null;
    feeScheduleId: string;
    onClose: () => void;
}

const AddEditCptDialog: React.FC<AddEditCptDialogProps> = ({
    open,
    editingCpt,
    feeScheduleId,
    onClose,
}) => {
    const [submitError, setSubmitError] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [upsertCpt, { isLoading: isSubmitting }] = useUpsertCptMutation();
    const [deleteCpt, { isLoading: isDeleting }] = useDeleteCptMutation();

    const {
        formData,
        formErrors,
        handleFormChange,
        validateForm
    } = useCptForm({
        editingCpt,
        feeScheduleId,
        open
    });

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            const cptData: CptView = {
                id: editingCpt?.id || '',
                feeScheduleId,
                code: formData.code.trim(),
                name: formData.name.trim(),
                category: formData.category.trim(),
                apcCode: formData.apcCode.trim(),
                baseRate: formData.baseRate,
                grouperPointer: formData.grouperPointer.trim(),
                multiProcedureDiscount: formData.multiProcedureDiscount,
                relativeWeight: formData.relativeWeight,
            };

            await upsertCpt({ cpt: cptData }).unwrap();
            onClose();
        } catch (error: any) {
            console.error('Failed to save CPT:', error);
            setSubmitError(
                error?.data?.message ||
                error?.message ||
                'Failed to save CPT. Please try again.'
            );
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!editingCpt?.id) return;

        try {
            await deleteCpt({
                id: editingCpt.id,
                feeScheduleId: editingCpt.feeScheduleId
            }).unwrap();
            onClose();
        } catch (error: any) {
            console.error('Failed to delete CPT:', error);
            setSubmitError(
                error?.data?.message ||
                error?.message ||
                'Failed to delete CPT. Please try again.'
            );
        } finally {
            setShowDeleteConfirm(false);
        }
    };

    const isEditing = !!editingCpt;
    const dialogTitle = isEditing ? 'Edit CPT Code' : 'Add New CPT Code';

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        {submitError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {submitError}
                            </Alert>
                        )}

                        <Stack spacing={3}>
                            <CodeApcFields
                                code={formData.code}
                                apcCode={formData.apcCode}
                                errors={formErrors}
                                onChange={(field, value) => handleFormChange(field, value)}
                            />

                            <TextField
                                label="Name"
                                value={formData.name}
                                onChange={(e) => handleFormChange('name', e.target.value)}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                size="small"
                                fullWidth
                                inputProps={{ maxLength: 500 }}
                            />

                            <CategoryGrouperFields
                                category={formData.category}
                                grouperPointer={formData.grouperPointer}
                                errors={formErrors}
                                onChange={(field, value) => handleFormChange(field, value)}
                            />

                            <RateWeightFields
                                baseRate={formData.baseRate}
                                relativeWeight={formData.relativeWeight}
                                errors={formErrors}
                                onBaseRateChange={(value) => handleFormChange('baseRate', value)}
                                onRelativeWeightChange={(value) => handleFormChange('relativeWeight', value)}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.multiProcedureDiscount}
                                        onChange={(e) => handleFormChange('multiProcedureDiscount', e.target.checked)}
                                    />
                                }
                                label="Apply Multi-Procedure Discount"
                            />
                        </Stack>
                    </Box>
                </DialogContent>

                <DialogActionButtons
                    isEditing={isEditing}
                    canDelete={!!editingCpt?.id}
                    isSubmitting={isSubmitting}
                    isDeleting={isDeleting}
                    onDelete={handleDeleteClick}
                    onCancel={onClose}
                    onSubmit={handleSubmit}
                />
            </Dialog>

            <DeleteConfirmDialog
                open={showDeleteConfirm}
                cptCode={editingCpt?.code || ''}
                cptName={editingCpt?.name || ''}
                isDeleting={isDeleting}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </>
    );
};

export default AddEditCptDialog;
